'use client'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { AD_VARIANTS } from '@/lib/ad-manifest'

// Ad slots render images from /public/ads/{slot}.png (or {slot}-{n}.png).
// The manifest is regenerated automatically by predev/prebuild.
// See docs/AD_NETWORK_STRATEGY.md.

interface SlotProps {
  slot: string
  label: string
  width: number
  height: number
  className?: string
  // Optional rotation index for deterministic cycling (e.g. from a feed
  // position). Omit to pick a variant at random after mount (avoids hydration mismatch).
  index?: number
}

function pickStart(variants: readonly string[], index: number | undefined): number {
  if (variants.length === 0) return 0
  if (typeof index === 'number') return ((index % variants.length) + variants.length) % variants.length
  // Deterministic for SSR + first client paint; randomized in useEffect when index omitted.
  return 0
}

function AdSlot({ slot, label, width, height, className = '', index }: SlotProps) {
  const variants = AD_VARIANTS[slot] ?? []
  const [cursor, setCursor] = useState(() => pickStart(variants, index))
  const [tries, setTries] = useState(0)
  const exhausted = variants.length === 0 || tries >= variants.length
  const src = variants[cursor]

  useEffect(() => {
    if (typeof index === 'number' || variants.length <= 1) return
    setCursor(Math.floor(Math.random() * variants.length))
  }, [index, variants.length])

  const onError = () => {
    // Try the next variant; if we've tried them all, fall back to placeholder.
    setCursor((c) => (c + 1) % Math.max(variants.length, 1))
    setTries((t) => t + 1)
  }

  const frame = (
    <div
      className="relative w-full overflow-hidden rounded border border-slate-200 bg-[#1E4289] shadow-sm transition-shadow hover:shadow-md"
      style={{ aspectRatio: `${width} / ${height}` }}
    >
      {!exhausted && src ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          key={src}
          src={src}
          alt={label}
          className="absolute inset-0 h-full w-full object-cover"
          onError={onError}
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white/80 select-none px-4">
          <span className="text-sm font-bold text-[#FEDB1A]">Advertise Here</span>
          <span className="mt-1 text-xs text-white/70">{label}</span>
        </div>
      )}
    </div>
  )

  return (
    <div
      className={`mx-auto my-4 ${className}`}
      style={{ maxWidth: width }}
      aria-label="Advertisement"
    >
      <Link href="/advertise" title="Advertise with RateWDW" className="block">
        {frame}
      </Link>
    </div>
  )
}

interface SlotInstanceProps {
  className?: string
  index?: number
}

// Sidebar / in-content. IAB Medium Rectangle.
export function AdRectangle({ className = '', index }: SlotInstanceProps) {
  return <AdSlot slot="rectangle" label="Advertise Here — Medium Rectangle" width={300} height={250} className={className} index={index} />
}

// Top-of-page or between-sections leaderboard. IAB Leaderboard.
export function AdBanner({ className = '', index }: SlotInstanceProps) {
  return <AdSlot slot="banner" label="Advertise Here — Leaderboard" width={728} height={90} className={className} index={index} />
}

// Native / in-feed promo. Typical native unit dimensions.
export function AdInFeed({ className = '', index }: SlotInstanceProps) {
  return <AdSlot slot="infeed" label="Advertise Here — In-Feed" width={600} height={200} className={className} index={index} />
}

// Between-sections, full content width. Renders a leaderboard on >=md screens
// and a medium rectangle on mobile so layout stays stable on both.
export function AdResponsive({ className = '', index }: SlotInstanceProps) {
  return (
    <div className={className}>
      <div className="hidden md:block">
        <AdSlot slot="responsive-desktop" label="Advertise Here" width={728} height={90} index={index} />
      </div>
      <div className="md:hidden">
        <AdSlot slot="responsive-mobile" label="Advertise Here" width={300} height={250} index={index} />
      </div>
    </div>
  )
}
